import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import Modal from "../Atomos/Modals/Modal";
import Button from "./Button";
import { TrashCan } from "../Atomos/Icons/TrashCan";
import SaveChanges from "../Atomos/Icons/EditMode/SaveChanges";
import Close from "../Atomos/Icons/Close";
import { useStaffMode } from "../../hooks/StaffModeProvider";

export type FormActionBarMode = "add" | "edit";

export type FormActionBarProps = {
  mode: FormActionBarMode;
  isDirty: boolean;
  dirtyFieldLabels?: string[];
  onSave: () => void;
  onDelete?: () => void | Promise<void>;
  disabled?: boolean;
  /** When true, Delete stays visible but cannot be clicked (e.g. temporary disable). */
  deleteDisabled?: boolean;
  onNavigateBack?: () => void;
  className?: string;
  showDeleteButton?: boolean;
  deleteConfirmMessage?: string;
};

export default function FormActionBar({
  mode,
  isDirty,
  dirtyFieldLabels = [],
  onSave,
  onDelete,
  disabled = false,
  deleteDisabled = false,
  onNavigateBack,
  className = "",
  showDeleteButton = true,
  deleteConfirmMessage = "Are you sure you want to delete this item?",
}: FormActionBarProps) {
  const navigate = useNavigate();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [unsavedModalOpen, setUnsavedModalOpen] = useState(false);
  const { isStaffMode } = useStaffMode();

  const goBack = useCallback(() => {
    if (onNavigateBack) {
      onNavigateBack();
    }
    else {
      navigate(-1);
    }
  }, [navigate, onNavigateBack]);

  const handleCancel = () => {
    if (!isDirty) {
      goBack();
      return;
    }
    setUnsavedModalOpen(true);
  };

  const showDelete =
    mode === "edit" && typeof onDelete === "function" && showDeleteButton;

  const listItems =
    dirtyFieldLabels.length > 0 ? (
      <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-gray-700">
        {dirtyFieldLabels.map((label) => (
          <li key={label}>{label}</li>
        ))}
      </ul>
    ) : (
      <p className="mt-2 text-sm text-gray-600">
        Several fields were modified.
      </p>
    );

  return (
    <>
      <div
        className={`sticky top-0 z-20 mb-4 flex w-full flex-wrap items-center justify-between gap-3 bg-white p-6 ${className}`.trim()}
      >
        {showDelete && (
          <Button
            text="Delete"
            onClick={() => setDeleteModalOpen(true)}
            color="primary"
            variant="filled"
            size="fixed"
            disabled={disabled || deleteDisabled}
            icon={<TrashCan />}
          />
        )}
        <div className="flex flex-1 flex-wrap items-center justify-end gap-3">
          <Button
            text="Cancel"
            onClick={handleCancel}
            color="neutral"
            variant="outlined"
            size="fixed"
            disabled={disabled}
            icon={<Close />}
          />
          <Button
            text={mode === "add" ? "Save" : "Update"}
            onClick={onSave}
            color={isStaffMode ? "secondary" : "primary"}
            variant="filled"
            size="fixed"
            disabled={disabled}
            icon={<SaveChanges />}
          />
        </div>
      </div>

      <Modal
        isModalOpen={unsavedModalOpen}
        toggleModal={() => setUnsavedModalOpen(false)}
        title="Unsaved changes"
        compact
        className="max-w-md"
      >
        <p className="text-gray-800">
          There are unsaved changes. Do you want to leave the page?
        </p>
        {isDirty && listItems}
        <div className="mt-6 flex flex-wrap justify-end gap-3">
          <Button
            text="Stay"
            onClick={() => setUnsavedModalOpen(false)}
            color="secondary"
            variant="filled"
            size="fixed"
          />
          <Button
            text="Leave"
            onClick={() => {
              setUnsavedModalOpen(false);
              goBack();
            }}
            color="neutral"
            variant="outlined"
            size="fixed"
          />
        </div>
      </Modal>

      <Modal
        isModalOpen={deleteModalOpen}
        toggleModal={() => setDeleteModalOpen(false)}
        title="Delete"
        compact
        className="max-w-md"
      >
        <p className="text-gray-800">{deleteConfirmMessage}</p>
        <div className="mt-6 flex flex-wrap justify-end gap-3">
          <Button
            text="Cancel"
            onClick={() => setDeleteModalOpen(false)}
            color="neutral"
            variant="outlined"
            size="fixed"
          />
          <Button
            text="Delete"
            onClick={() => {
              setDeleteModalOpen(false);
              void Promise.resolve(onDelete?.());
            }}
            color={isStaffMode ? "secondary" : "primary"}
            variant="filled"
            size="fixed"
          />
        </div>
      </Modal>
    </>
  );
}
